import { useState, useRef, useEffect } from 'react';
import { message as antdMessage } from 'antd';

const ChatApp = () => {

   const [messages, setMessages] = useState([
       { role: 'ai', content: '我是一个博学的智能聊天助手，您请说聊点什么吧！' }
   ]);
   const [loading, setLoading] = useState(false);
   const inputRef = useRef(null);
   const messagesContainerRef = useRef(null);

   const aiQueueRef = useRef([]);
   const typingRef = useRef(false);
   const aiMessageRef = useRef('');
   const streamBufferRef = useRef('');
   const [menuIndex, setMenuIndex] = useState(null);

   const scrollToBottom = (behavior = 'auto') => {
       const el = messagesContainerRef.current;
       if (!el) return;
       try {
           el.scrollTo({ top: el.scrollHeight, behavior });
       } catch (e) {
           // fallback
           el.scrollTop = el.scrollHeight;
       }
   };

   useEffect(() => {
       // auto-scroll when messages change (including during typing)
       scrollToBottom('smooth');
   }, [messages]);

   // Set page title while this component is mounted, restore on unmount
   useEffect(() => {
       const prevTitle = document.title;
       document.title = 'AI好问好答 影音艺';
       return () => { document.title = prevTitle; };
   }, []);

   // Close the per-message menu when clicking outside
   useEffect(() => {
       const onDocClick = () => setMenuIndex(null);
       document.addEventListener('click', onDocClick);
       return () => document.removeEventListener('click', onDocClick);
   }, []);

   const typeFromQueue = async (charDelay = 20) => {
       if (typingRef.current) return;
       typingRef.current = true;

       while (aiQueueRef.current.length > 0) {
           const chunk = aiQueueRef.current.shift();
           for (let i = 0; i < chunk.length; i++) {
               aiMessageRef.current += chunk[i];

               setMessages(prev => {
                   const lastMessage = prev[prev.length - 1];
                   // If last message is ai, replace it; otherwise append a new ai message
                   return lastMessage?.role === 'ai'
                       ? [...prev.slice(0, -1), { role: 'ai', content: aiMessageRef.current }]
                       : [...prev, { role: 'ai', content: aiMessageRef.current }];
               });

               // wait a bit to create typing effect
               await new Promise(resolve => setTimeout(resolve, charDelay));
           }
       }

       typingRef.current = false;
   };

   // Parse SSE-style events from the accumulated stream buffer.
   // Supports CRLF or LF separators, multiple `data:` lines per event, and preserves spaces within data lines.
   const processSSEBuffer = (flush = false) => {
       let buffer = streamBufferRef.current;
       // Regex to find first blank-line separator (CRLFCRLF or LFLF)
       const sepRegex = /\r?\n\r?\n/;

       while (true) {
           const match = buffer.match(sepRegex);
           if (!match) break;

           const sepIndex = match.index;
           const eventText = buffer.slice(0, sepIndex);
           buffer = buffer.slice(sepIndex + match[0].length);

           // Parse lines in this event
           const lines = eventText.split(/\r?\n/);
           const dataParts = [];
           for (const line of lines) {
               if (line.startsWith('data:')) {
                   // Keep the text after 'data:' including leading space if present
                   dataParts.push(line.slice(5).replace(/^ /, ''));
               }
               // ignore other fields (event:, id:, etc.) for now
           }

           if (dataParts.length === 0) {
               // empty event, skip
               continue;
           }

           const fullData = dataParts.join('\n').trim();

           // Common SSE sentinel to indicate stream end
           if (fullData === '[DONE]') {
               // Nothing to push, signal end if needed
               continue;
           }

           if (fullData) {
               aiQueueRef.current.push(fullData);
               // trigger typing if not already
               typeFromQueue();
           }
       }

       // If flushing at the end and there's remaining partial data without a trailing blank line,
       // treat it as an event too.
       if (flush && buffer.trim()) {
           const lines = buffer.split(/\r?\n/);
           const dataParts = [];
           for (const line of lines) {
               if (line.startsWith('data:')) dataParts.push(line.slice(5).replace(/^ /, ''));
           }
           const fullData = dataParts.join('\n').trim();
           if (fullData && fullData !== '[DONE]') {
               aiQueueRef.current.push(fullData);
               typeFromQueue();
           }
           buffer = '';
       }

       streamBufferRef.current = buffer;
   };

   const sendMessage = async () => {

       const userMessage = inputRef.current.value;

       if (!userMessage.trim()) return;

       // append user message and immediately focus & scroll
       setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
       // ensure the input is cleared and focused for the next message
       inputRef.current.value = '';
       // set loading state to disable multiple sends
       setLoading(true);
       try {
           // focus input and scroll to bottom so user sees the latest message
           try { inputRef.current.focus(); } catch (e) {}
           scrollToBottom('smooth');

           // Insert an empty ai message placeholder so UI shows a bubble immediately
           setMessages(prev => [...prev, { role: 'ai', content: '' }]);
           aiMessageRef.current = '';
           aiQueueRef.current = [];
           streamBufferRef.current = '';

           const response = await fetch('https://ai.yingyinyi.com/ai/api/chat', {
               method: 'POST',
               body: userMessage,
               headers: { 'Content-Type': 'application/json' }
           });

           const reader = response.body.getReader();
           const decoder = new TextDecoder('utf-8');

           while (true) {
               const { done, value } = await reader.read();
               if (done) break;

               // decode with stream support and append to buffer
               const chunkText = decoder.decode(value, { stream: true });
               streamBufferRef.current += chunkText;

               // process any complete SSE events currently in the buffer
               processSSEBuffer(false);
           }

           // process any remaining partial data as a final event
           processSSEBuffer(true);

       } catch (error) {

           setMessages(prev => [...prev, { role: 'ai', content: "抱歉，服务暂时不可用" }]);

       } finally {
          setLoading(false);
       }

   };

   // Save a message content as a note. Accepts a string content and posts JSON (UTF-8).
   const addNote = async (content) => {
       if (!content || !content.toString().trim()) {
           antdMessage.warn('内容为空，无法保存');
           return;
       }

       console.log("content:" + content);

       let host = process.env.REACT_APP_HOST || '';

       const payload = {
           content: content,
           ai: true
       };

       let formData = new FormData()
       formData.append('content', content)
       console.log("host:" + host);

       fetch(host + "/site/buddy/note/api/addJsonApi", {
           method: 'POST',
           headers: {
               'Content-Type': 'application/json; charset=UTF-8'
           },
           body: JSON.stringify(payload),
//            body: formData,
           credentials: 'include'
       })
           .then(response => response.json())
           .then(data => {
               if(data.success) {
                   antdMessage.info("发送成功！")
               } else {
                   antdMessage.error("发送失败！" + (data.msg || '未知错误'))
               }
           })

   };

   return (

       <div style={{ maxWidth: '800px', margin: '0 auto' }}>

           <div ref={messagesContainerRef} style={{ height: '600px', overflowY: 'auto', padding: '20px', paddingBottom: '120px' }}>

               {messages.map((msg, index) => (

                   <div key={index} style={{ margin: '20px 0', padding: '10px', borderRadius: '8px',
                       backgroundColor: msg.role === 'user' ? '#e3f2fd' : '#f5f5f5',
                       position: 'relative'
                    }}
                    onClick={() => setMenuIndex(null)}
                   >

                       <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>

                       {/* menu trigger */}
                       <button
                           onClick={(e) => { e.stopPropagation(); setMenuIndex(prev => prev === index ? null : index); }}
                           aria-label="更多选项"
                           style={{ position: 'absolute', right: 0, top: -5, border: 'none', background: 'transparent', cursor: 'pointer' }}
                       >
                           ⋯
                       </button>

                       {/* small dropdown menu */}
                       {menuIndex === index && (
                           <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 8, top: 34, background: '#fff', border: '1px solid #e8e8e8', boxShadow: '0 2px 6px rgba(0,0,0,0.12)', zIndex: 2000 }}>
                               <div style={{ padding: '8px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { addNote(msg.content); setMenuIndex(null); }}>
                                   保存
                               </div>
                           </div>
                       )}

                   </div>

               ))}

           </div>

           <div id="input-area" style={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: '800px',
                boxSizing: 'border-box',
                 display: 'flex',
                 gap: '10px',
                 padding: '10px',
                 background: '#fff',
                 boxShadow: '0 -1px 6px rgba(0,0,0,0.08)',
                 zIndex: 1000
             }}>

                <input
                    ref={inputRef}
                    onKeyDown={(e) => {
                        // allow Shift+Enter for newline; ignore Enter during IME composition
                        const isComposing = e.nativeEvent && e.nativeEvent.isComposing;
                        // prevent sending while loading
                        if (loading) { e.preventDefault(); return; }
                        if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                    style={{ flex: 1, padding: '10px' }}
                    placeholder="输入问题..."
                />

                <button
                    onClick={() => { if (!loading) sendMessage(); }}
                    disabled={loading}
                    style={{ padding: '10px 10px', opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    {loading && (
                        // simple inline SVG spinner using SMIL animation
                        <svg width="16" height="16" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="25" cy="25" r="20" fill="none" stroke="#1976d2" strokeWidth="4" strokeLinecap="round" strokeDasharray="31.4 31.4">
                                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                            </circle>
                        </svg>
                    )}
                    <span>{loading ? '稍等..' : '发送'}</span>
                </button>

             </div>

          </div>

      );

 };


 export default ChatApp;

